/**
 * Story 062 — TanStack Query hooks for the component-models feature.
 *
 * Mirrors `apps/web/src/features/turbine-models/queries/hooks.ts` shape.
 * Lifts the `assetsFetch` helper from Sites (same `/api/assets/v1`
 * namespace).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { assetsFetch } from "../../sites/queries/assetsFetch.js";
import { assetComponentModelsKeys, componentGroupsKeys } from "./keys.js";
import type {
  AssetComponentModelDetail,
  CreateAssetComponentModelBody,
  ListAssetComponentModelsExpandedParams,
  ListAssetComponentModelsExpandedResponse,
  ListAssetComponentModelsLeanResponse,
  ListComponentGroupsResponse,
  UpdateAssetComponentModelBody,
} from "./types.js";

function listUrl(params: ListAssetComponentModelsExpandedParams): string {
  const sp = new URLSearchParams();
  // `expanded=true` is the discriminator that routes the request to
  // the expanded list-handler (spec 074) vs spec 079's lean
  // cascading-dropdown projection.
  sp.set("expanded", "true");
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 25));
  if (params.search && params.search.length > 0) {
    sp.set("search", params.search);
  }
  if (params.sort) sp.set("sort", params.sort);
  if (params.includeInactive === true) sp.set("includeInactive", "true");
  // R58-W-06 (assets/v1@4.0.0+): canonical category axis is
  // `componentGroupId` (UUID CSV) — alphabetical join for determinism.
  if (params.componentGroupId && params.componentGroupId.length > 0) {
    const sorted = [...params.componentGroupId].sort((a, b) =>
      a.localeCompare(b),
    );
    sp.set("componentGroupId", sorted.join(","));
  }
  if (params.manufacturer && params.manufacturer.length > 0) {
    const sorted = [...params.manufacturer].sort((a, b) => a.localeCompare(b));
    sp.set("manufacturer", sorted.join(","));
  }
  if (params.scope !== undefined) sp.set("scope", params.scope);
  return `/asset-component-models?${sp.toString()}`;
}

export function useListComponentModelsQuery(
  params: ListAssetComponentModelsExpandedParams,
) {
  return useQuery<ListAssetComponentModelsExpandedResponse>({
    queryKey: assetComponentModelsKeys.list(params),
    queryFn: () =>
      assetsFetch<ListAssetComponentModelsExpandedResponse>(listUrl(params)),
    staleTime: 30_000,
    retry: false,
  });
}

export function useGetComponentModelQuery(id: string | null | undefined) {
  return useQuery<AssetComponentModelDetail>({
    queryKey: assetComponentModelsKeys.detail(id ?? "__none__"),
    queryFn: () =>
      assetsFetch<AssetComponentModelDetail>(
        `/asset-component-models/${id ?? ""}`,
      ),
    enabled: typeof id === "string" && id.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export function useCreateComponentModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAssetComponentModelBody) =>
      assetsFetch<AssetComponentModelDetail>("/asset-component-models", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: assetComponentModelsKeys.lists(),
      });
    },
  });
}

/**
 * SB2-W-07 (assets/v1@4.5.0) — global-scope create. Sends `tenantId: null`
 * so the BE writes to `public.global_asset_component_models`
 * (platform_admin only).
 */
export function useCreateGlobalComponentModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAssetComponentModelBody) =>
      assetsFetch<AssetComponentModelDetail>("/asset-component-models", {
        method: "POST",
        body: { ...body, tenantId: null },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: assetComponentModelsKeys.lists(),
      });
    },
  });
}

export interface UpdateComponentModelArgs {
  id: string;
  body: UpdateAssetComponentModelBody;
}

export function useUpdateComponentModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: UpdateComponentModelArgs) =>
      assetsFetch<AssetComponentModelDetail>(`/asset-component-models/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({
        queryKey: assetComponentModelsKeys.lists(),
      });
      void qc.invalidateQueries({
        queryKey: assetComponentModelsKeys.detail(updated.id),
      });
    },
  });
}

export function useDeactivateComponentModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      assetsFetch<AssetComponentModelDetail>(`/asset-component-models/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({
        queryKey: assetComponentModelsKeys.lists(),
      });
      void qc.invalidateQueries({
        queryKey: assetComponentModelsKeys.detail(updated.id),
      });
    },
  });
}

export function useReactivateComponentModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      assetsFetch<AssetComponentModelDetail>(
        `/asset-component-models/${id}/reactivate`,
        { method: "POST" },
      ),
    onSuccess: (updated) => {
      void qc.invalidateQueries({
        queryKey: assetComponentModelsKeys.lists(),
      });
      void qc.invalidateQueries({
        queryKey: assetComponentModelsKeys.detail(updated.id),
      });
    },
  });
}

/**
 * Spec 079 — `GET /api/assets/v1/asset-component-models?lean=true`.
 *
 * Lightweight projection consumed by Story 061's cascading Component
 * Model picker in the Components Add modal. Returns the union of the
 * tenant catalogue + global catalogue with `scope: 'tenant' | 'global'`
 * on each row so the FE can group "Frequently used" / "All component
 * models" in the Autocomplete (mirrors Story 057's asset-model picker).
 */
export function useListComponentModelsLeanQuery(opts?: {
  componentGroupId?: string;
}) {
  const componentGroupId = opts?.componentGroupId;
  return useQuery<ListAssetComponentModelsLeanResponse>({
    queryKey: [
      ...assetComponentModelsKeys.all,
      "lean",
      componentGroupId ?? "__all__",
    ] as const,
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set("lean", "true");
      if (componentGroupId) sp.set("componentGroupId", componentGroupId);
      return assetsFetch<ListAssetComponentModelsLeanResponse>(
        `/asset-component-models?${sp.toString()}`,
      );
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Spec 080 — `GET /api/assets/v1/component-groups[?assetTypeId=…]`.
 * Cacheable, near-static rollup that drives the Component Group
 * Autocomplete in the Add / View-Edit modals.
 */
export function useListComponentGroupsQuery(assetTypeId?: string) {
  return useQuery<ListComponentGroupsResponse>({
    queryKey: componentGroupsKeys.list(assetTypeId),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (assetTypeId) sp.set("assetTypeId", assetTypeId);
      const qs = sp.toString();
      return assetsFetch<ListComponentGroupsResponse>(
        `/component-groups${qs ? `?${qs}` : ""}`,
      );
    },
    staleTime: 10 * 60_000, // 10 min — near-static catalogue.
    retry: false,
  });
}
